import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { DangerButton } from '../../components/buttons';
import { Loading } from '../../components/loading';
import type { ProcessingResult, ReceivingSessionState } from '../../data/receiving';
import { canCancelSession, partitionUnknownProducts, sessionVendors } from '../../data/receiving';
import type { Companion } from '../../data/uploader-connection';
import { integrationLabels } from '../../data/vendor';
import { useCompanionConnections } from '../../hooks/uploads';
import { useReceivingSession } from '../../hooks/use-receiving-session';
import { useVendorIntegrations, useVendors } from '../../hooks/vendors';
import { receivingSessionRoute, storeLayout } from '../../route-apis';
import { ActionPane } from './components/action-pane';
import { CancelSessionModal } from './components/cancel-session-modal';
import { DuplicateItemCodesTable } from './components/duplicate-item-codes-table';
import { GuidePane } from './components/guide-pane';
import { ProgressBar } from './components/progress-bar';
import { ReadOnlyNotice } from './components/read-only-notice';
import { ReviewSection } from './components/review-results';
import { SessionEnded } from './components/session-ended';
import { UnknownProductsTable } from './components/unknown-products-table';
import { useCancelSession } from './hooks/use-cancel-session';

export function ReceivingSessionPage() {
  const { storeId, sessionId } = receivingSessionRoute.useParams();
  const { receivingService } = storeLayout.useRouteContext();
  const navigate = useNavigate();
  const { vendors } = useVendors();
  const integrations = useVendorIntegrations();
  const { connections } = useCompanionConnections();

  const { loaded, readOnly, state, context, applySession } = useReceivingSession({
    storeId,
    sessionId,
    service: receivingService,
  });

  const cancelSession = useCancelSession(storeId, receivingService);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Track whether we have ever had a session (to distinguish "not found on
  // initial load" from "session disappeared while the tab was open").
  const everHadSessionRef = useRef(false);
  if (context.session) {
    everHadSessionRef.current = true;
  }

  // If no session on initial load → redirect to the list (clean fresh start).
  // If session disappears after we had one → show SessionEnded (spec §10).
  useEffect(() => {
    if (loaded && !context.session && !everHadSessionRef.current) {
      void navigate({ to: '/stores/$storeId/receiving', params: { storeId } });
    }
  }, [loaded, context.session, navigate, storeId]);

  if (!loaded) {
    return <Loading />;
  }

  // Terminal "ended" screen. Two cases are the same to the user: a session that
  // DISAPPEARED mid-session (pre-Confirm Cancel deletes the row → context.session
  // goes null) and one ABANDONED to the terminal `cancelled` state (the row is
  // kept for audit, so context.session stays set). Both mean the receiving is
  // over — show the ended screen so the user can start fresh (spec §10) rather
  // than a blank action pane. (complete keeps its richer Complete screen via the
  // ActionPane.)
  if (state === 'cancelled' || (!context.session && everHadSessionRef.current)) {
    return (
      <div className="receiving-layout h-full w-full flex flex-col gap-4 py-4 px-4">
        <SessionEnded storeId={storeId} />
      </div>
    );
  }

  if (!context.session) {
    return null;
  }

  const activeSessionId = context.session.session_id;

  // NOT wrapped in useMemo: this sits after the early returns above, so a
  // hook here would be called conditionally (Rules of Hooks).
  const vendorsForSession = sessionVendors(context.session, vendors);

  // One vendor reads as its own name; a group reads as the integration plus a
  // count, because listing ten rep names in the header is unusable. The
  // integration is named from the catalog, never by its normalized_name code.
  const groupName = vendorsForSession[0]?.normalized_name ?? '';
  const vendorName =
    vendorsForSession.length === 0
      ? null
      : vendorsForSession.length === 1
        ? vendorsForSession[0].name
        : `${integrationLabels(integrations).get(groupName) ?? groupName} (${vendorsForSession.length} vendors)`;

  // Feeds the Vendor provenance column and duplicate-item-code vendor suffix —
  // both stay hidden when the session has only one vendor (ORD-395).
  const vendorNames = new Map(vendorsForSession.map((v) => [v.vendor_id, v.name] as const));

  const liveCompanion = connections.find(
    (c) => c.uploader?.machine_id === context.session?.machine_id
  );
  const companionName = liveCompanion?.hostname ?? context.session.machine_id ?? '';
  const companionConnected = Boolean(liveCompanion);
  const companions = connections.filter((c): c is Companion => Boolean(c.uploader?.machine_id));

  const isReview = state === 'review';
  const unknownProducts =
    (context.session.processing_result as ProcessingResult | undefined)?.unknown_products ?? [];
  const { duplicateItemCodes, noMatch: noMatchProducts } =
    partitionUnknownProducts(unknownProducts);
  const hasUnknownProductsInLpos = state === 'waiting_for_lpos' && unknownProducts.length > 0;
  const isFullWidth = isReview;

  const panesClass = isFullWidth
    ? 'receiving-panes flex-1 min-h-0 overflow-auto'
    : hasUnknownProductsInLpos
      ? 'receiving-panes grid grid-cols-2 gap-4 px-4'
      : 'receiving-panes flex-1 grid grid-cols-2 gap-4 min-h-0 px-4';

  return (
    <div
      className={`receiving-layout h-full w-full flex flex-col gap-4 py-4${hasUnknownProductsInLpos ? ' overflow-auto' : ''}`}
    >
      <ProgressBar
        state={state}
        failedFrom={context.session.failed_from}
        pausedFrom={context.session.paused_from}
        companionName={companionName}
        companionConnected={companionConnected}
        vendorName={vendorName}
        files={context.session.files}
      />
      {readOnly && <ReadOnlyNotice />}
      <div className={panesClass}>
        <ActionPane
          state={state}
          context={context}
          readOnly={readOnly}
          service={receivingService}
          applySession={applySession}
          companionConnected={companionConnected}
          companionName={companionName}
          companions={companions}
        />
        {!isFullWidth && <GuidePane state={state} vendorId={context.session.vendor_id} />}
      </div>
      {!readOnly &&
        canCancelSession(
          state as ReceivingSessionState,
          context.session.paused_from ?? undefined
        ) && (
          <div className="flex justify-end px-4 pt-2">
            <DangerButton onClick={() => setShowCancelConfirm(true)}>Cancel session</DangerButton>
          </div>
        )}
      {hasUnknownProductsInLpos && duplicateItemCodes.length > 0 && (
        <ReviewSection
          tone="warn"
          title="Duplicate item codes"
          count={duplicateItemCodes.length}
          description="Two products in LiquorPOS share the same item code. Often one of them is an older duplicate you've disabled. Give one a different item code in LiquorPOS, run an upload from your Companion, then process again."
          defaultOpen
        >
          <DuplicateItemCodesTable rows={duplicateItemCodes} vendorNames={vendorNames} />
        </ReviewSection>
      )}
      {hasUnknownProductsInLpos && noMatchProducts.length > 0 && (
        <ReviewSection
          tone="warn"
          title="Unknown products"
          count={noMatchProducts.length}
          description="Not in LiquorPOS. Enter these manually to complete your receiving."
          defaultOpen
        >
          <UnknownProductsTable rows={noMatchProducts} />
        </ReviewSection>
      )}
      <CancelSessionModal
        visible={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          void cancelSession(activeSessionId);
        }}
        body="Are you sure you want to cancel this session? Uploaded invoices will be discarded."
      />
    </div>
  );
}
